import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { AssignmentLate as AssignmentLateIcon } from '@mui/icons-material';
import SessionDetailDrawer from 'components/session/SessionDetailDrawer';
import PerformanceCard from 'components/performance/PerformanceCard';
import PerformanceDetailPanel from 'components/performance/PerformanceDetailPanel';

/**
 * Rendimientos dinámicos como LISTA de filas a lo ancho + drawer derecho de detalle
 * (idéntico a "Mis sesiones"). Reemplaza la tabla/grid anterior. Lo usan tanto el
 * entrenador (PerformanceList) como cliente/admin (DynamicPerformancePage).
 */
function DynamicPerformanceDataGrid({ performances, onEdit, onDelete, loading = false }) {
  const { t } = useTranslation(['performances']);
  const [selectedId, setSelectedId] = useState(null);

  const selected = useMemo(
    () => (performances || []).find((p) => String(p.id) === String(selectedId)) || null,
    [performances, selectedId]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: 'gold.main' }} />
      </Box>
    );
  }

  if (!performances || performances.length === 0) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center', backgroundColor: 'surface.raised', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main', borderRadius: 3 }}>
        <Stack spacing={2} alignItems="center">
          <AssignmentLateIcon sx={{ fontSize: 64, color: 'gold.soft' }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {t('dynamicPerformances.noDataTitle', { ns: 'performances' })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('dynamicPerformances.noDataMessage', { ns: 'performances' })}
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Stack spacing={1.5}>
        {performances.map((row) => (
          <PerformanceCard
            key={row.id}
            row={row}
            selected={String(row.id) === String(selectedId)}
            onView={() => setSelectedId(row.id)}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </Stack>

      <SessionDetailDrawer open={Boolean(selected)} onClose={() => setSelectedId(null)}>
        <PerformanceDetailPanel
          row={selected}
          onEdit={(r) => { setSelectedId(null); onEdit(r); }}
          onDelete={(r) => { setSelectedId(null); onDelete(r); }}
          onClose={() => setSelectedId(null)}
        />
      </SessionDetailDrawer>
    </>
  );
}

DynamicPerformanceDataGrid.propTypes = {
  performances: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default DynamicPerformanceDataGrid;
